import { useState, useEffect, useCallback } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { useConnectionStore } from '@/stores/connectionStore'
import { useAIStore, CLAUDE_MODELS, EMBEDDING_PROVIDERS, COACHING_STYLES, type EmbeddingProvider } from '@/stores/aiStore'
import { useProjectStore } from '@/stores/projectStore'
import { useAuthStore } from '@/stores/authStore'
import { clearGraph } from '@/lib/api/sparql-adapter'
import { settingsApi, type FusekiConfigResponse } from '@/lib/api/client'
import { useQueryClient } from '@tanstack/react-query'
import { FacetConfigEditor } from './FacetConfigEditor'
import { EntityRulesEditor } from './EntityRulesEditor'
import { ExecutionLogsPage } from '@/components/execution-logs/ExecutionLogsPage'
import { AIWorkflowSettings } from './AIWorkflowSettings'
import { SchemaBrowser } from '@/components/browser/SchemaBrowser'
import { InstanceBrowser } from '@/components/instances/InstanceBrowser'
import { QueryEditor } from '@/components/query/QueryEditor'
import { AIModelConfig } from './AIModelConfig'
import {
  Database,
  Brain,
  XCircle,
  Loader2,
  Eye,
  EyeOff,
  Save,
  RefreshCw,
  Trash2,
  Circle,
  AlertTriangle,
  HardDrive,
  CheckCircle,
  Cloud,
  Zap,
  FileText,
  MessageSquare,
  Settings2,
  Plug,
  Sparkles,
  Shield,
  Activity,
  Workflow,
  ChevronDown,
  Wrench,
  Layers,
  FileCode,
  Image,
  Server,
  Cpu,
  Map,
} from 'lucide-react'
import { JourneySettings } from './JourneySettings'
import { ArtifactSettings } from './ArtifactSettings'
import { PhaseConfigurator } from './PhaseConfigurator'
import { useSettingsNavigationStore } from '@/stores/settingsStore'

// ============================================================================
// SETTINGS PAGE TYPES
// ============================================================================

type SettingsTab = 'sparql' | 'ai-api' | 'workflows' | 'logs' | 'facets' | 'rules' | 'artifacts' | 'schema' | 'instances' | 'query' | 'journey' | 'phase-config'

interface NavItem {
  id: SettingsTab
  label: string
  icon: React.ReactNode
  description: string
}

interface CategoryConfig {
  id: string
  label: string
  icon: React.ReactNode
  color: string
  items: NavItem[]
}

const SETTINGS_CATEGORIES: CategoryConfig[] = [
  {
    id: 'integration',
    label: 'Integration',
    icon: <Plug className="w-4 h-4" />,
    color: '#5c8a6b', // Market color
    items: [
      {
        id: 'sparql',
        label: 'SPARQL Connection',
        icon: <Database className="w-4 h-4" />,
        description: 'Graph database connection',
      },
      {
        id: 'ai-api',
        label: 'Claude API',
        icon: <Brain className="w-4 h-4" />,
        description: 'AI configuration and models',
      },
    ],
  },
  {
    id: 'ai-workflows',
    label: 'AI Workflows',
    icon: <Workflow className="w-4 h-4" />,
    color: '#3d7a8c', // Business color
    items: [
      {
        id: 'workflows',
        label: 'Workflows',
        icon: <Workflow className="w-4 h-4" />,
        description: 'Manage AI workflows, prompts, and agents',
      },
      {
        id: 'logs',
        label: 'Execution Logs',
        icon: <Activity className="w-4 h-4" />,
        description: 'View AI workflow execution history',
      },
    ],
  },
  {
    id: 'ontology',
    label: 'Ontology',
    icon: <Settings2 className="w-4 h-4" />,
    color: '#c9a857', // Solution color
    items: [
      {
        id: 'facets',
        label: 'Facet Categories',
        icon: <Settings2 className="w-4 h-4" />,
        description: 'Customize facet types and mappings',
      },
      {
        id: 'rules',
        label: 'Entity Rules',
        icon: <Shield className="w-4 h-4" />,
        description: 'Semantic validation and inference rules',
      },
    ],
  },
  {
    id: 'advanced',
    label: 'Advanced',
    icon: <Wrench className="w-4 h-4" />,
    color: '#7c7c7c', // Neutral gray
    items: [
      {
        id: 'schema',
        label: 'Schema',
        icon: <Database className="w-4 h-4" />,
        description: 'Browse and manage ontology schema',
      },
      {
        id: 'instances',
        label: 'Instances',
        icon: <Layers className="w-4 h-4" />,
        description: 'View and manage instances',
      },
      {
        id: 'query',
        label: 'Query',
        icon: <FileCode className="w-4 h-4" />,
        description: 'Execute SPARQL queries',
      },
    ],
  },
  {
    id: 'workspace',
    label: 'Workspace Configuration',
    icon: <Settings2 className="w-4 h-4" />,
    color: '#3d7a8c', // Business color
    items: [
      {
        id: 'journey',
        label: 'Journey Framework',
        icon: <Map className="w-4 h-4" />,
        description: 'Configure PMF discovery journey',
      },
      {
        id: 'phase-config',
        label: 'Phase Configurator',
        icon: <Layers className="w-4 h-4" />,
        description: 'Configure phase artifacts and entities',
      },
      {
        id: 'artifacts',
        label: 'Artifact Templates',
        icon: <Layers className="w-4 h-4" />,
        description: 'Configure artifact types and templates',
      },
    ],
  },
]

// ============================================================================
// MAIN SETTINGS PAGE
// ============================================================================

export function SettingsPage() {
  const [activeTab, setActiveTab] = useState<SettingsTab>('sparql')
  const [openCategories, setOpenCategories] = useState<Set<string>>(
    new Set(['integration']) // Default: Integration category open
  )

  // Listen for navigation requests from other components (e.g., Execution Logs)
  const targetTab = useSettingsNavigationStore((state) => state.targetTab)

  useEffect(() => {
    if (targetTab) {
      setActiveTab(targetTab)
      // Open the appropriate category
      const category = SETTINGS_CATEGORIES.find(c => c.items.some(item => item.id === targetTab))
      if (category) {
        setOpenCategories((prev) => new Set(prev).add(category.id))
      }
      // Don't clear navigation here - let AIWorkflowSettings handle section/prompt navigation
    }
  }, [targetTab])

  // Toggle category open/closed
  const toggleCategory = (categoryId: string) => {
    setOpenCategories((prev) => {
      const next = new Set(prev)
      if (next.has(categoryId)) {
        next.delete(categoryId)
      } else {
        next.add(categoryId)
      }
      return next
    })
  }

  // When clicking a tab, auto-expand its category
  const handleTabClick = (tabId: SettingsTab, categoryId: string) => {
    setActiveTab(tabId)
    setOpenCategories((prev) => new Set(prev).add(categoryId))
  }

  // Get active item details
  const activeItem = SETTINGS_CATEGORIES.flatMap((c) => c.items).find(
    (item) => item.id === activeTab
  )

  return (
    <div style={{ height: '100%', width: '100%', display: 'flex', overflow: 'hidden' }}>
      {/* Settings Navigation Sidebar */}
      <div style={{ width: '260px', flexShrink: 0, backgroundColor: 'white', borderRight: '1px solid var(--color-warm-200)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '16px', borderBottom: '1px solid var(--color-warm-200)' }}>
          <h2 style={{ fontWeight: 600, fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Settings2 style={{ width: '20px', height: '20px', color: 'var(--color-market)' }} />
            Settings
          </h2>
        </div>
        <nav style={{ flex: 1, overflowY: 'auto', padding: '8px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {SETTINGS_CATEGORIES.map((category) => {
              const isOpen = openCategories.has(category.id)
              const hasActiveItem = category.items.some((item) => item.id === activeTab)
              const itemCount = category.items.length

              return (
                <div key={category.id}>
                  {/* Category Header Button */}
                  <button
                    onClick={() => toggleCategory(category.id)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: hasActiveItem ? 500 : 400,
                      textAlign: 'left',
                      border: 'none',
                      cursor: 'pointer',
                      backgroundColor: hasActiveItem ? `${category.color}08` : 'transparent',
                      color: hasActiveItem ? category.color : 'var(--color-text-secondary)',
                      transition: 'all 150ms ease-in-out',
                    }}
                    onMouseEnter={(e) => {
                      if (!hasActiveItem) {
                        e.currentTarget.style.backgroundColor = 'var(--color-warm-100)'
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!hasActiveItem) {
                        e.currentTarget.style.backgroundColor = 'transparent'
                      }
                    }}
                  >
                    {/* Chevron */}
                    <ChevronDown
                      style={{
                        width: '16px',
                        height: '16px',
                        color: hasActiveItem ? category.color : 'var(--color-text-muted)',
                        transform: isOpen ? 'rotate(0deg)' : 'rotate(-90deg)',
                        transition: 'transform 200ms ease-in-out',
                      }}
                    />

                    {/* Category Icon */}
                    <div
                      style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '6px',
                        backgroundColor: `${category.color}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <span style={{ color: category.color }}>{category.icon}</span>
                    </div>

                    {/* Category Label */}
                    <span style={{ flex: 1 }}>{category.label}</span>

                    {/* Item Count Badge */}
                    <Badge
                      variant="secondary"
                      style={{
                        fontSize: '11px',
                        height: '18px',
                        minWidth: '18px',
                        padding: '0 6px',
                        backgroundColor: hasActiveItem ? `${category.color}20` : 'var(--color-warm-200)',
                        color: hasActiveItem ? category.color : 'var(--color-text-muted)',
                        border: 'none',
                      }}
                    >
                      {itemCount}
                    </Badge>
                  </button>

                  {/* Sub-items (when expanded) */}
                  {isOpen && (
                    <div
                      style={{
                        marginLeft: '26px',
                        paddingLeft: '12px',
                        borderLeft: `2px solid ${category.color}30`,
                        marginTop: '4px',
                        marginBottom: '4px',
                      }}
                    >
                      {category.items.map((item) => {
                        const isActive = activeTab === item.id

                        return (
                          <button
                            key={item.id}
                            onClick={() => handleTabClick(item.id, category.id)}
                            style={{
                              width: '100%',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              fontSize: '13px',
                              textAlign: 'left',
                              border: 'none',
                              cursor: 'pointer',
                              backgroundColor: isActive ? `${category.color}15` : 'transparent',
                              color: isActive ? category.color : 'var(--color-text-secondary)',
                              fontWeight: isActive ? 500 : 400,
                              marginBottom: '2px',
                              transition: 'all 150ms ease-in-out',
                            }}
                            onMouseEnter={(e) => {
                              if (!isActive) {
                                e.currentTarget.style.backgroundColor = 'var(--color-warm-100)'
                                e.currentTarget.style.color = 'var(--color-text)'
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isActive) {
                                e.currentTarget.style.backgroundColor = 'transparent'
                                e.currentTarget.style.color = 'var(--color-text-secondary)'
                              }
                            }}
                          >
                            {/* Item Icon */}
                            <span style={{ color: isActive ? category.color : 'var(--color-text-muted)' }}>
                              {item.icon}
                            </span>

                            {/* Item Label */}
                            <span>{item.label}</span>
                          </button>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </nav>
      </div>

      {/* Settings Content */}
      {/* Full-width views */}
      {activeTab === 'logs' ? (
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <ExecutionLogsPage />
        </div>
      ) : activeTab === 'workflows' ? (
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <AIWorkflowSettings />
        </div>
      ) : activeTab === 'schema' ? (
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <SchemaBrowser />
        </div>
      ) : activeTab === 'instances' ? (
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <InstanceBrowser />
        </div>
      ) : activeTab === 'query' ? (
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <QueryEditor />
        </div>
      ) : activeTab === 'artifacts' ? (
        <div style={{ flex: 1, overflow: 'hidden' }}>
          <ArtifactSettings />
        </div>
      ) : (
        /* Standard content views with padding */
        <div style={{ flex: 1, overflowY: 'auto', overflowX: 'hidden', backgroundColor: 'var(--color-warm-100)', padding: '24px' }}>
          <div style={{ width: '100%', maxWidth: '800px' }}>
            {/* Page Header */}
            <div style={{ marginBottom: '24px' }}>
              <h1 style={{ fontSize: '18px', fontWeight: 600 }}>
                {activeItem?.label}
              </h1>
              <p style={{ fontSize: '14px', color: 'var(--color-text-muted)', marginTop: '4px' }}>
                {activeItem?.description}
              </p>
            </div>

            {/* Tab Content */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {activeTab === 'journey' && <JourneySettings />}
              {activeTab === 'phase-config' && <PhaseConfigurator />}
              {activeTab === 'sparql' && <IntegrationsSettings />}
              {activeTab === 'ai-api' && <AISettings />}
              {activeTab === 'facets' && <FacetConfigEditor />}
              {activeTab === 'rules' && <EntityRulesEditor />}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ============================================================================
// INTEGRATIONS SETTINGS
// ============================================================================

function IntegrationsSettings() {
  return (
    <div className="space-y-6">
      <FusekiSettings />
      <DataManagement />
    </div>
  )
}

function FusekiSettings() {
  const {
    isConnected,
    isConnecting,
    error,
    connect,
    disconnect,
  } = useConnectionStore()

  // Fuseki config state
  const [config, setConfig] = useState<FusekiConfigResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [isTesting, setIsTesting] = useState(false)
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null)
  const [configError, setConfigError] = useState<string | null>(null)

  // Form state
  const [formUrl, setFormUrl] = useState('')
  const [formDataset, setFormDataset] = useState('')
  const [formUsername, setFormUsername] = useState('')
  const [formPassword, setFormPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)

  // Load current config on mount
  useEffect(() => {
    const loadConfig = async () => {
      try {
        setIsLoading(true)
        const response = await settingsApi.getFusekiConfig()
        setConfig(response.config)
        setFormUrl(response.config.url)
        setFormDataset(response.config.dataset)
        setFormUsername(response.config.username)
        setFormPassword('') // Don't show existing password
      } catch (err) {
        setConfigError(err instanceof Error ? err.message : 'Failed to load config')
      } finally {
        setIsLoading(false)
      }
    }
    loadConfig()
  }, [])

  // Track changes
  useEffect(() => {
    if (!config) return
    const changed =
      formUrl !== config.url ||
      formDataset !== config.dataset ||
      formUsername !== config.username ||
      formPassword !== ''
    setHasChanges(changed)
  }, [formUrl, formDataset, formUsername, formPassword, config])

  const handleSave = async () => {
    setIsSaving(true)
    setTestResult(null)
    setConfigError(null)
    try {
      const updateData: { url?: string; dataset?: string; username?: string; password?: string } = {}
      if (formUrl !== config?.url) updateData.url = formUrl
      if (formDataset !== config?.dataset) updateData.dataset = formDataset
      if (formUsername !== config?.username) updateData.username = formUsername
      if (formPassword) updateData.password = formPassword

      const response = await settingsApi.updateFusekiConfig(updateData)
      setConfig(response.config)
      setFormPassword('') // Clear password field after save
      setHasChanges(false)
      setTestResult({ success: true, message: 'Configuration saved successfully' })
    } catch (err) {
      setConfigError(err instanceof Error ? err.message : 'Failed to save config')
    } finally {
      setIsSaving(false)
    }
  }

  const handleTestConnection = async () => {
    setIsTesting(true)
    setTestResult(null)
    try {
      // Test with current form values
      const result = await settingsApi.testFusekiConnection({
        url: formUrl,
        dataset: formDataset,
        username: formUsername,
        password: formPassword || undefined,
      })
      setTestResult(result)
    } catch (err) {
      setTestResult({ success: false, message: err instanceof Error ? err.message : 'Connection test failed' })
    } finally {
      setIsTesting(false)
    }
  }

  const handleConnect = async () => {
    await connect()
  }

  if (isLoading) {
    return (
      <Card className="w-full">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-warm-100 flex items-center justify-center flex-shrink-0">
              <Database className="w-5 h-5 text-warm-700" />
            </div>
            <div className="min-w-0">
              <CardTitle className="text-base font-semibold whitespace-nowrap">Fuseki Connection</CardTitle>
              <CardDescription className="text-text-muted whitespace-nowrap">Loading configuration...</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2 text-text-muted">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Loading Fuseki configuration...</span>
          </div>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="w-full">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0 flex-1">
            <div className="w-10 h-10 rounded-lg bg-warm-100 flex items-center justify-center flex-shrink-0">
              <Database className="w-5 h-5 text-warm-700" />
            </div>
            <div className="min-w-0">
              <CardTitle className="text-base font-semibold whitespace-nowrap">Fuseki Connection</CardTitle>
              <CardDescription className="text-text-muted whitespace-nowrap">Configure SPARQL triplestore connection</CardDescription>
            </div>
          </div>
          <ConnectionStatus isConnected={isConnected} isConnecting={isConnecting} />
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {(error || configError) && (
          <div className="flex items-center gap-3 px-4 py-3 rounded-lg text-sm bg-solution/10 text-solution border border-solution/20">
            <XCircle className="w-4 h-4 flex-shrink-0" />
            <span>{error || configError}</span>
          </div>
        )}

        {testResult && (
          <div className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm ${
            testResult.success
              ? 'bg-business/10 text-business border border-business/20'
              : 'bg-solution/10 text-solution border border-solution/20'
          }`}>
            {testResult.success ? (
              <CheckCircle className="w-4 h-4 flex-shrink-0" />
            ) : (
              <XCircle className="w-4 h-4 flex-shrink-0" />
            )}
            <span>{testResult.message}</span>
          </div>
        )}

        {/* Fuseki URL */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-text-secondary">Fuseki URL</label>
          <input
            type="text"
            value={formUrl}
            onChange={(e) => setFormUrl(e.target.value)}
            placeholder="http://localhost:3030"
            className="w-full px-4 py-2.5 text-sm border border-border-subtle rounded-lg bg-warm-50 focus:outline-none focus:ring-2 focus:ring-warm-400 focus:border-transparent font-mono placeholder:text-text-muted"
          />
          <p className="text-xs text-text-muted">
            The base URL of your Apache Jena Fuseki server
          </p>
        </div>

        {/* Dataset */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-text-secondary">Dataset Name</label>
          <input
            type="text"
            value={formDataset}
            onChange={(e) => setFormDataset(e.target.value)}
            placeholder="ispot2"
            className="w-full px-4 py-2.5 text-sm border border-border-subtle rounded-lg bg-warm-50 focus:outline-none focus:ring-2 focus:ring-warm-400 focus:border-transparent font-mono placeholder:text-text-muted"
          />
          <p className="text-xs text-text-muted">
            The name of the dataset to query (e.g., ispot2)
          </p>
        </div>

        {/* Username */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-text-secondary">Username</label>
          <input
            type="text"
            value={formUsername}
            onChange={(e) => setFormUsername(e.target.value)}
            placeholder="admin"
            className="w-full px-4 py-2.5 text-sm border border-border-subtle rounded-lg bg-warm-50 focus:outline-none focus:ring-2 focus:ring-warm-400 focus:border-transparent placeholder:text-text-muted"
          />
        </div>

        {/* Password */}
        <div className="space-y-2">
          <label className="block text-sm font-medium text-text-secondary">
            Password {config?.hasPassword && <span className="text-text-muted font-normal">(currently set)</span>}
          </label>
          <div className="relative">
            <input
              type={showPassword ? 'text' : 'password'}
              value={formPassword}
              onChange={(e) => setFormPassword(e.target.value)}
              placeholder={config?.hasPassword ? '••••••••' : 'Enter password'}
              className="w-full px-4 py-2.5 text-sm border border-border-subtle rounded-lg bg-warm-50 focus:outline-none focus:ring-2 focus:ring-warm-400 focus:border-transparent placeholder:text-text-muted pr-10"
            />
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </Button>
          </div>
          <p className="text-xs text-text-muted">
            Leave blank to keep the current password
          </p>
        </div>

        {/* Info box */}
        <div className="p-4 bg-warm-50 rounded-lg border border-border-subtle">
          <div className="flex items-center gap-3">
            <Cloud className="w-5 h-5 text-business" />
            <div>
              <p className="text-sm font-medium text-text">Project Isolation</p>
              <p className="text-xs text-text-muted mt-1">
                Each project has its own named graph in the triplestore.
                Select a project from the sidebar to query its data.
              </p>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-3 pt-3 border-t border-border-subtle">
          <Button
            onClick={handleSave}
            disabled={!hasChanges || isSaving}
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                Save Configuration
              </>
            )}
          </Button>

          <Button
            variant="outline"
            onClick={handleTestConnection}
            disabled={isTesting}
          >
            {isTesting ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Testing...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4" />
                Test Connection
              </>
            )}
          </Button>

          {isConnected && (
            <Button variant="outline" onClick={disconnect}>
              Disconnect
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  )
}

function DataManagement() {
  const { isConnected } = useConnectionStore()
  const queryClient = useQueryClient()
  const [isClearing, setIsClearing] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [result, setResult] = useState<{ success: boolean; message: string } | null>(null)

  const handleClearData = async () => {
    setIsClearing(true)
    setResult(null)

    try {
      await clearGraph()
      // Invalidate all queries to refresh the UI
      queryClient.invalidateQueries()
      setResult({ success: true, message: 'All data has been cleared successfully!' })
      setShowConfirm(false)
    } catch (err) {
      setResult({
        success: false,
        message: err instanceof Error ? err.message : 'Failed to clear data',
      })
    } finally {
      setIsClearing(false)
    }
  }

  return (
    <Card className="w-full">
      <CardHeader className="pb-4">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-lg bg-solution/10 flex items-center justify-center flex-shrink-0">
            <HardDrive className="w-5 h-5 text-solution" />
          </div>
          <div className="min-w-0">
            <CardTitle className="text-base font-semibold whitespace-nowrap">Data Management</CardTitle>
            <CardDescription className="text-text-muted whitespace-nowrap">Manage triplestore data</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {!isConnected ? (
          <p className="text-sm text-text-muted">
            Connect to Fuseki to manage data.
          </p>
        ) : (
          <>
            {result && (
              <div
                className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm ${
                  result.success
                    ? 'bg-business/10 text-business border border-business/20'
                    : 'bg-solution/10 text-solution border border-solution/20'
                }`}
              >
                {result.success ? (
                  <CheckCircle className="w-4 h-4 flex-shrink-0" />
                ) : (
                  <XCircle className="w-4 h-4 flex-shrink-0" />
                )}
                {result.message}
              </div>
            )}

            {showConfirm ? (
              <div className="p-4 bg-solution/5 border border-solution/20 rounded-lg space-y-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-solution flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-solution">Clear All Data?</p>
                    <p className="text-sm text-text-muted mt-1">
                      This will permanently delete all triples from the triplestore.
                      This action cannot be undone.
                    </p>
                  </div>
                </div>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowConfirm(false)}
                    disabled={isClearing}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleClearData}
                    disabled={isClearing}
                    className="bg-solution hover:bg-solution-dark text-white"
                  >
                    {isClearing ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Clearing...
                      </>
                    ) : (
                      <>
                        <Trash2 className="w-4 h-4" />
                        Yes, Clear All Data
                      </>
                    )}
                  </Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between p-4 bg-warm-50 rounded-lg border border-border-subtle">
                <div>
                  <p className="font-medium text-text">Clear All Data</p>
                  <p className="text-sm text-text-muted">
                    Remove all instances and triples from the triplestore
                  </p>
                </div>
                <Button
                  variant="outline"
                  onClick={() => setShowConfirm(true)}
                  className="text-solution hover:text-solution hover:border-solution"
                >
                  <Trash2 className="w-4 h-4" />
                  Clear Data
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}

// ============================================================================
// AI SETTINGS
// ============================================================================

function AISettings() {
  const {
    apiKey,
    isConfigured,
    setApiKey,
    clearApiKey,
    coachingStyle,
    setCoachingStyle,
  } = useAIStore()

  const [tempApiKey, setTempApiKey] = useState('')
  const [showKey, setShowKey] = useState(false)
  const [isEditing, setIsEditing] = useState(!isConfigured)

  const handleSave = () => {
    if (tempApiKey.trim()) {
      setApiKey(tempApiKey.trim())
      setTempApiKey('')
      setIsEditing(false)
    }
  }

  const handleClear = () => {
    clearApiKey()
    setTempApiKey('')
    setIsEditing(true)
  }

  const maskedKey = apiKey ? `${apiKey.slice(0, 10)}...${apiKey.slice(-4)}` : ''

  return (
    <div className="space-y-6">
      {/* API Key Card */}
      <Card className="w-full">
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0 flex-1">
              <div className="w-10 h-10 rounded-lg bg-business/10 flex items-center justify-center flex-shrink-0">
                <Brain className="w-5 h-5 text-business" />
              </div>
              <div className="min-w-0">
                <CardTitle className="text-base font-semibold whitespace-nowrap">Claude API Key</CardTitle>
                <CardDescription className="text-text-muted whitespace-nowrap">Anthropic API for semantic extraction</CardDescription>
              </div>
            </div>
            {isConfigured ? (
              <Badge className="bg-business/10 text-business border border-business/20 flex-shrink-0">Configured</Badge>
            ) : (
              <Badge variant="outline" className="flex-shrink-0">Not configured</Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          <p className="text-sm text-text-muted leading-relaxed">
            Claude AI enables intelligent extraction of business entities from PDF documents.
            The API key is stored securely in your browser's local storage.
          </p>

          {isConfigured && !isEditing ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3 px-4 py-3 bg-warm-50 rounded-lg border border-border-subtle">
                <span className="text-sm font-mono flex-1 text-text-secondary">
                  {showKey ? apiKey : maskedKey}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowKey(!showKey)}
                  className="h-8 w-8"
                >
                  {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
              </div>
              <div className="flex gap-3">
                <Button variant="secondary" onClick={() => setIsEditing(true)}>
                  Change Key
                </Button>
                <Button variant="outline" onClick={handleClear}>
                  <Trash2 className="w-4 h-4" />
                  Remove
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-secondary mb-2">API Key</label>
                <input
                  type="password"
                  value={tempApiKey}
                  onChange={(e) => setTempApiKey(e.target.value)}
                  placeholder="sk-ant-api03-..."
                  className="w-full px-4 py-2.5 text-sm border border-border-subtle rounded-lg bg-warm-50 focus:outline-none focus:ring-2 focus:ring-warm-400 focus:border-transparent font-mono placeholder:text-text-muted"
                />
              </div>
              <div className="flex gap-3">
                <Button onClick={handleSave} disabled={!tempApiKey.trim()}>
                  <Save className="w-4 h-4" />
                  Save API Key
                </Button>
                {isConfigured && (
                  <Button variant="outline" onClick={() => setIsEditing(false)}>
                    Cancel
                  </Button>
                )}
              </div>
            </div>
          )}

          <div className="pt-4 border-t border-border-subtle">
            <p className="text-xs text-text-muted">
              Get your API key from{' '}
              <a
                href="https://console.anthropic.com/settings/keys"
                target="_blank"
                rel="noopener noreferrer"
                className="text-warm-700 hover:text-warm-900 hover:underline font-medium"
              >
                console.anthropic.com
              </a>
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Coaching Style Card */}
      {isConfigured && (
        <Card className="w-full">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-market/10 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-5 h-5 text-market" />
              </div>
              <div className="min-w-0">
                <CardTitle className="text-base font-semibold whitespace-nowrap">Coaching Style</CardTitle>
                <CardDescription className="text-text-muted whitespace-nowrap">Choose your coaching experience</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              {COACHING_STYLES.map((style) => {
                const isActive = coachingStyle === style.id
                return (
                  <button
                    key={style.id}
                    onClick={() => setCoachingStyle(style.id)}
                    className={`flex flex-col items-start gap-1 p-4 rounded-lg border transition-all text-left ${
                      isActive
                        ? 'border-market bg-market/5'
                        : 'border-border-subtle hover:border-warm-300 bg-warm-50'
                    }`}
                  >
                    <span className={`text-sm font-medium ${isActive ? 'text-market' : 'text-text-secondary'}`}>
                      {style.name}
                    </span>
                    <span className="text-xs text-text-muted">{style.description}</span>
                  </button>
                )
              })}
            </div>
            <p className="text-xs text-text-muted">
              <strong>Classic:</strong> Direct questions with quality assessments.
              <strong> Collaborative:</strong> Warm coaching with frameworks, acknowledgments, and scaffolded guidance.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Per-Operation Model Config */}
      <AIModelConfig />

      {/* Visual Analysis Settings */}
      <VisualAnalysisSettings />

      {/* Embedding Provider Settings */}
      <EmbeddingProviderSettings />
    </div>
  )
}

// ============================================================================
// EMBEDDING PROVIDER SETTINGS
// ============================================================================

function EmbeddingProviderSettings() {
  const {
    embeddingProvider,
    openaiApiKey,
    mistralApiKey,
    ollamaUrl,
    setEmbeddingProvider,
    setOpenAIApiKey,
    setMistralApiKey,
    setOllamaUrl,
  } = useAIStore()

  const [showOpenAIKey, setShowOpenAIKey] = useState(false)
  const [showMistralKey, setShowMistralKey] = useState(false)
  const [tempOpenAIKey, setTempOpenAIKey] = useState('')
  const [tempMistralKey, setTempMistralKey] = useState('')

  const selectedProvider = EMBEDDING_PROVIDERS.find((p) => p.id === embeddingProvider)
  const isConfigured =
    embeddingProvider === 'ollama' ||
    (embeddingProvider === 'openai' && openaiApiKey) ||
    (embeddingProvider === 'mistral' && mistralApiKey)

  const handleSaveOpenAI = () => {
    if (tempOpenAIKey.trim()) {
      setOpenAIApiKey(tempOpenAIKey.trim())
      setTempOpenAIKey('')
    }
  }

  const handleSaveMistral = () => {
    if (tempMistralKey.trim()) {
      setMistralApiKey(tempMistralKey.trim())
      setTempMistralKey('')
    }
  }

  const maskKey = (key: string) => (key ? `${key.slice(0, 8)}...${key.slice(-4)}` : '')

  return (
    <Card className="w-full">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0 flex-1">
            <div className="w-10 h-10 rounded-lg bg-market/10 flex items-center justify-center flex-shrink-0">
              <Cpu className="w-5 h-5 text-market" />
            </div>
            <div className="min-w-0">
              <CardTitle className="text-base font-semibold whitespace-nowrap">
                Embedding Provider
              </CardTitle>
              <CardDescription className="text-text-muted whitespace-nowrap">
                Vector embeddings for Knowledge Base RAG
              </CardDescription>
            </div>
          </div>
          {isConfigured ? (
            <Badge className="bg-business/10 text-business border border-business/20 flex-shrink-0">
              {selectedProvider?.name}
            </Badge>
          ) : (
            <Badge variant="outline" className="flex-shrink-0 text-solution">
              Not configured
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-sm text-text-muted leading-relaxed">
          Choose an embedding provider for the Knowledge Base. Ollama runs locally with no rate
          limits, while OpenAI and Mistral offer cloud-based options.
        </p>

        {/* Provider Selection */}
        <div className="space-y-3">
          <label className="text-sm font-medium text-text">Provider</label>
          <div className="grid grid-cols-3 gap-3">
            {EMBEDDING_PROVIDERS.map((provider) => {
              const isActive = embeddingProvider === provider.id
              const Icon = provider.id === 'ollama' ? Server : Cloud
              return (
                <button
                  key={provider.id}
                  onClick={() => setEmbeddingProvider(provider.id)}
                  className={`flex flex-col items-center gap-2 p-4 rounded-lg border transition-all ${
                    isActive
                      ? 'border-business bg-business/5'
                      : 'border-border-subtle hover:border-warm-300 bg-warm-50'
                  }`}
                >
                  <Icon
                    className={`w-5 h-5 ${isActive ? 'text-business' : 'text-text-muted'}`}
                  />
                  <span
                    className={`text-sm font-medium ${isActive ? 'text-business' : 'text-text-secondary'}`}
                  >
                    {provider.name}
                  </span>
                  <span className="text-xs text-text-muted text-center">{provider.description}</span>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className="text-xs">
                      {provider.dimensions}d
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      {provider.rateLimit}
                    </Badge>
                  </div>
                </button>
              )
            })}
          </div>
        </div>

        {/* Provider-specific configuration */}
        {embeddingProvider === 'openai' && (
          <div className="space-y-3 p-4 bg-warm-50 rounded-lg border border-border-subtle">
            <div className="flex items-center gap-2">
              <Cloud className="w-4 h-4 text-text-muted" />
              <label className="text-sm font-medium text-text">OpenAI API Key</label>
            </div>
            {openaiApiKey ? (
              <div className="flex items-center gap-2">
                <span className="text-sm font-mono flex-1 text-text-secondary">
                  {showOpenAIKey ? openaiApiKey : maskKey(openaiApiKey)}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowOpenAIKey(!showOpenAIKey)}
                  className="h-8 w-8"
                >
                  {showOpenAIKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
                <Button variant="outline" size="sm" onClick={() => setOpenAIApiKey('')}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <input
                  type="password"
                  value={tempOpenAIKey}
                  onChange={(e) => setTempOpenAIKey(e.target.value)}
                  placeholder="sk-proj-..."
                  className="flex-1 px-3 py-2 text-sm border border-border-subtle rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-warm-400 font-mono"
                />
                <Button onClick={handleSaveOpenAI} disabled={!tempOpenAIKey.trim()}>
                  <Save className="w-4 h-4" />
                </Button>
              </div>
            )}
            <p className="text-xs text-text-muted">
              Get your key from{' '}
              <a
                href="https://platform.openai.com/api-keys"
                target="_blank"
                rel="noopener noreferrer"
                className="text-warm-700 hover:underline"
              >
                platform.openai.com
              </a>
            </p>
          </div>
        )}

        {embeddingProvider === 'mistral' && (
          <div className="space-y-3 p-4 bg-warm-50 rounded-lg border border-border-subtle">
            <div className="flex items-center gap-2">
              <Cloud className="w-4 h-4 text-text-muted" />
              <label className="text-sm font-medium text-text">Mistral API Key</label>
            </div>
            {mistralApiKey ? (
              <div className="flex items-center gap-2">
                <span className="text-sm font-mono flex-1 text-text-secondary">
                  {showMistralKey ? mistralApiKey : maskKey(mistralApiKey)}
                </span>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowMistralKey(!showMistralKey)}
                  className="h-8 w-8"
                >
                  {showMistralKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </Button>
                <Button variant="outline" size="sm" onClick={() => setMistralApiKey('')}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <input
                  type="password"
                  value={tempMistralKey}
                  onChange={(e) => setTempMistralKey(e.target.value)}
                  placeholder="..."
                  className="flex-1 px-3 py-2 text-sm border border-border-subtle rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-warm-400 font-mono"
                />
                <Button onClick={handleSaveMistral} disabled={!tempMistralKey.trim()}>
                  <Save className="w-4 h-4" />
                </Button>
              </div>
            )}
            <p className="text-xs text-text-muted">
              Get your key from{' '}
              <a
                href="https://console.mistral.ai/api-keys/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-warm-700 hover:underline"
              >
                console.mistral.ai
              </a>
            </p>
          </div>
        )}

        {embeddingProvider === 'ollama' && (
          <div className="space-y-3 p-4 bg-warm-50 rounded-lg border border-border-subtle">
            <div className="flex items-center gap-2">
              <Server className="w-4 h-4 text-text-muted" />
              <label className="text-sm font-medium text-text">Ollama URL</label>
            </div>
            <input
              type="text"
              value={ollamaUrl}
              onChange={(e) => setOllamaUrl(e.target.value)}
              placeholder="http://localhost:11434"
              className="w-full px-3 py-2 text-sm border border-border-subtle rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-warm-400 font-mono"
            />
            <div className="text-xs text-text-muted space-y-1">
              <p>
                <strong>Local setup:</strong> <code>http://localhost:11434</code>
              </p>
              <p>
                <strong>From Docker:</strong> <code>http://host.docker.internal:11434</code>
              </p>
              <p className="mt-2">
                Install Ollama from{' '}
                <a
                  href="https://ollama.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-warm-700 hover:underline"
                >
                  ollama.ai
                </a>
                , then run: <code>ollama pull nomic-embed-text</code>
              </p>
            </div>
          </div>
        )}

        {/* Warning about dimension mismatch */}
        <div className="flex items-start gap-3 p-3 bg-solution/5 border border-solution/20 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-solution flex-shrink-0 mt-0.5" />
          <div className="text-xs text-text-secondary">
            <strong>Note:</strong> Each provider uses different embedding dimensions. If you switch
            providers, existing knowledge base content will need to be re-processed.
          </div>
        </div>
      </CardContent>
    </Card>
  )
}

// ============================================================================
// VISUAL ANALYSIS SETTINGS
// ============================================================================

function VisualAnalysisSettings() {
  const { currentAccount } = useAuthStore()
  const { currentProject, updateProject } = useProjectStore()
  const [isUpdating, setIsUpdating] = useState(false)

  const handleToggle = async (enabled: boolean) => {
    if (!currentAccount || !currentProject) return

    setIsUpdating(true)
    try {
      await updateProject(currentAccount.id, currentProject.id, { visualAnalysisEnabled: enabled })
    } catch (err) {
      console.error('Failed to update visual analysis setting:', err)
    } finally {
      setIsUpdating(false)
    }
  }

  if (!currentProject) {
    return (
      <Card className="w-full">
        <CardHeader className="pb-4">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-solution/10 flex items-center justify-center flex-shrink-0">
              <Image className="w-5 h-5 text-solution" />
            </div>
            <div className="min-w-0">
              <CardTitle className="text-base font-semibold whitespace-nowrap">Visual Analysis</CardTitle>
              <CardDescription className="text-text-muted whitespace-nowrap">Analyze images in PDF documents</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-text-muted">
            Select a project to configure visual analysis settings.
          </p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card className="w-full">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0 flex-1">
            <div className="w-10 h-10 rounded-lg bg-solution/10 flex items-center justify-center flex-shrink-0">
              <Image className="w-5 h-5 text-solution" />
            </div>
            <div className="min-w-0">
              <CardTitle className="text-base font-semibold whitespace-nowrap">Visual Analysis</CardTitle>
              <CardDescription className="text-text-muted whitespace-nowrap">Analyze images in PDF documents</CardDescription>
            </div>
          </div>
          {currentProject.visualAnalysisEnabled ? (
            <Badge className="bg-business/10 text-business border border-business/20 flex-shrink-0">Enabled</Badge>
          ) : (
            <Badge variant="outline" className="flex-shrink-0">Disabled</Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        <p className="text-sm text-text-muted leading-relaxed">
          When enabled, AI will analyze images in uploaded PDFs to extract additional insights.
          This includes detecting logos, business canvases, diagrams, whiteboards, and other visual content.
        </p>

        <div className="flex items-center justify-between p-4 bg-warm-50 rounded-lg border border-border-subtle">
          <div>
            <p className="font-medium text-text">Enable Visual Analysis</p>
            <p className="text-sm text-text-muted">
              Analyze images in PDFs for entities and insights
            </p>
          </div>
          <Switch
            checked={currentProject.visualAnalysisEnabled ?? false}
            onCheckedChange={handleToggle}
            disabled={isUpdating}
          />
        </div>

        <div className="pt-4 border-t border-border-subtle">
          <p className="text-xs text-text-muted">
            Visual analysis uses additional API calls and may increase processing time.
            Best used for documents containing diagrams, logos, or visual frameworks.
          </p>
        </div>
      </CardContent>
    </Card>
  )
}

// ============================================================================
// SHARED COMPONENTS
// ============================================================================

function ConnectionStatus({ isConnected, isConnecting }: { isConnected: boolean; isConnecting: boolean }) {
  if (isConnecting) {
    return (
      <Badge variant="outline" className="flex items-center gap-1.5">
        <Loader2 className="w-3 h-3 animate-spin" />
        Connecting
      </Badge>
    )
  }

  if (isConnected) {
    return (
      <Badge className="bg-business/10 text-business border border-business/20 flex items-center gap-1.5">
        <Circle className="w-2 h-2 fill-current" />
        Connected
      </Badge>
    )
  }

  return (
    <Badge variant="outline" className="flex items-center gap-1.5 text-text-muted">
      <Circle className="w-2 h-2" />
      Disconnected
    </Badge>
  )
}
